// ---------------------------------------------------------------------------
// Client-side postcode lookup (UK / non-US only)
//
// UK: Postcode area (first 1-2 letters) -> city + county, offline, zero config.
//
// US ZIPs are NOT resolved here anymore. The old ZIP-3-prefix table returned a
// single regional hub city for a whole prefix (wrong for most ZIPs, WPS-126);
// it has been removed. US resolution now goes through the platform reference
// table via the async `AddressLookupAPI.lookupPostcode` (see
// `resolvePostcodeFill`), which returns the ZIP's real USPS city.
// ---------------------------------------------------------------------------

export interface PostcodeLookupResult {
  city: string
  state: string
  country: string
}


// ── UK: postcode area (first 1-2 letters) → [city, county] ─────────────────
const UK_AREAS: Record<string, [string, string]> = {
  AB: ['Aberdeen', 'Aberdeenshire'],
  AL: ['St Albans', 'Hertfordshire'],
  B: ['Birmingham', 'West Midlands'],
  BA: ['Bath', 'Somerset'],
  BB: ['Blackburn', 'Lancashire'],
  BD: ['Bradford', 'West Yorkshire'],
  BH: ['Bournemouth', 'Dorset'],
  BL: ['Bolton', 'Greater Manchester'],
  BN: ['Brighton', 'East Sussex'],
  BR: ['Bromley', 'Greater London'],
  BS: ['Bristol', 'Bristol'],
  BT: ['Belfast', 'Northern Ireland'],
  CA: ['Carlisle', 'Cumbria'],
  CB: ['Cambridge', 'Cambridgeshire'],
  CF: ['Cardiff', 'South Glamorgan'],
  CH: ['Chester', 'Cheshire'],
  CM: ['Chelmsford', 'Essex'],
  CO: ['Colchester', 'Essex'],
  CR: ['Croydon', 'Greater London'],
  CT: ['Canterbury', 'Kent'],
  CV: ['Coventry', 'West Midlands'],
  CW: ['Crewe', 'Cheshire'],
  DA: ['Dartford', 'Kent'],
  DD: ['Dundee', 'Angus'],
  DE: ['Derby', 'Derbyshire'],
  DG: ['Dumfries', 'Dumfries and Galloway'],
  DH: ['Durham', 'County Durham'],
  DL: ['Darlington', 'County Durham'],
  DN: ['Doncaster', 'South Yorkshire'],
  DT: ['Dorchester', 'Dorset'],
  DY: ['Dudley', 'West Midlands'],
  E: ['London', 'Greater London'],
  EC: ['London', 'Greater London'],
  EH: ['Edinburgh', 'Midlothian'],
  EN: ['Enfield', 'Greater London'],
  EX: ['Exeter', 'Devon'],
  FK: ['Falkirk', 'Stirlingshire'],
  FY: ['Blackpool', 'Lancashire'],
  G: ['Glasgow', 'Lanarkshire'],
  GL: ['Gloucester', 'Gloucestershire'],
  GU: ['Guildford', 'Surrey'],
  GY: ['St Peter Port', 'Guernsey'],
  HA: ['Harrow', 'Greater London'],
  HD: ['Huddersfield', 'West Yorkshire'],
  HG: ['Harrogate', 'North Yorkshire'],
  HP: ['Hemel Hempstead', 'Hertfordshire'],
  HR: ['Hereford', 'Herefordshire'],
  HS: ['Outer Hebrides', 'Western Isles'],
  HU: ['Hull', 'East Yorkshire'],
  HX: ['Halifax', 'West Yorkshire'],
  IG: ['Ilford', 'Greater London'],
  IM: ['Douglas', 'Isle of Man'],
  IP: ['Ipswich', 'Suffolk'],
  IV: ['Inverness', 'Highland'],
  JE: ['St Helier', 'Jersey'],
  KA: ['Kilmarnock', 'Ayrshire'],
  KT: ['Kingston upon Thames', 'Greater London'],
  KW: ['Kirkwall', 'Orkney'],
  KY: ['Kirkcaldy', 'Fife'],
  L: ['Liverpool', 'Merseyside'],
  LA: ['Lancaster', 'Lancashire'],
  LD: ['Llandrindod Wells', 'Powys'],
  LE: ['Leicester', 'Leicestershire'],
  LL: ['Llandudno', 'Gwynedd'],
  LN: ['Lincoln', 'Lincolnshire'],
  LS: ['Leeds', 'West Yorkshire'],
  LU: ['Luton', 'Bedfordshire'],
  M: ['Manchester', 'Greater Manchester'],
  ME: ['Rochester', 'Kent'],
  MK: ['Milton Keynes', 'Buckinghamshire'],
  ML: ['Motherwell', 'Lanarkshire'],
  N: ['London', 'Greater London'],
  NE: ['Newcastle upon Tyne', 'Tyne and Wear'],
  NG: ['Nottingham', 'Nottinghamshire'],
  NN: ['Northampton', 'Northamptonshire'],
  NP: ['Newport', 'Gwent'],
  NR: ['Norwich', 'Norfolk'],
  NW: ['London', 'Greater London'],
  OL: ['Oldham', 'Greater Manchester'],
  OX: ['Oxford', 'Oxfordshire'],
  PA: ['Paisley', 'Renfrewshire'],
  PE: ['Peterborough', 'Cambridgeshire'],
  PH: ['Perth', 'Perthshire'],
  PL: ['Plymouth', 'Devon'],
  PO: ['Portsmouth', 'Hampshire'],
  PR: ['Preston', 'Lancashire'],
  RG: ['Reading', 'Berkshire'],
  RH: ['Redhill', 'Surrey'],
  RM: ['Romford', 'Greater London'],
  S: ['Sheffield', 'South Yorkshire'],
  SA: ['Swansea', 'West Glamorgan'],
  SE: ['London', 'Greater London'],
  SG: ['Stevenage', 'Hertfordshire'],
  SK: ['Stockport', 'Greater Manchester'],
  SL: ['Slough', 'Berkshire'],
  SM: ['Sutton', 'Greater London'],
  SN: ['Swindon', 'Wiltshire'],
  SO: ['Southampton', 'Hampshire'],
  SP: ['Salisbury', 'Wiltshire'],
  SR: ['Sunderland', 'Tyne and Wear'],
  SS: ['Southend-on-Sea', 'Essex'],
  ST: ['Stoke-on-Trent', 'Staffordshire'],
  SW: ['London', 'Greater London'],
  SY: ['Shrewsbury', 'Shropshire'],
  TA: ['Taunton', 'Somerset'],
  TD: ['Galashiels', 'Scottish Borders'],
  TF: ['Telford', 'Shropshire'],
  TN: ['Tonbridge', 'Kent'],
  TQ: ['Torquay', 'Devon'],
  TR: ['Truro', 'Cornwall'],
  TS: ['Cleveland', 'North Yorkshire'],
  TW: ['Twickenham', 'Greater London'],
  UB: ['Southall', 'Greater London'],
  W: ['London', 'Greater London'],
  WA: ['Warrington', 'Cheshire'],
  WC: ['London', 'Greater London'],
  WD: ['Watford', 'Hertfordshire'],
  WF: ['Wakefield', 'West Yorkshire'],
  WN: ['Wigan', 'Greater Manchester'],
  WR: ['Worcester', 'Worcestershire'],
  WS: ['Walsall', 'West Midlands'],
  WV: ['Wolverhampton', 'West Midlands'],
  YO: ['York', 'North Yorkshire'],
  ZE: ['Lerwick', 'Shetland'],
}

// ── Public API ──────────────────────────────────────────────────────────────

export function lookupPostcode(code: string): PostcodeLookupResult | null {
  const cleaned = code.trim()
  if (!cleaned) return null

  // UK: outward code (A9, A9A, A99, AA9, AA9A, AA99) optionally followed
  // by a space and inward code (9AA). Extract the 1-2 letter area prefix.
  // The digit after the area letters distinguishes UK from plain text.
  const ukMatch = cleaned.toUpperCase().match(/^([A-Z]{1,2}\d[A-Z\d]?)\s*(\d[A-Z]{2})?$/)
  if (ukMatch) {
    const outward = ukMatch[1]
    const area = outward.match(/^([A-Z]{1,2})/)?.[1]
    if (area) {
      const entry = UK_AREAS[area]
      if (entry) return { city: entry[0], state: entry[1], country: 'United Kingdom' }
    }
  }

  return null
}

// ---------------------------------------------------------------------------
// US-aware resolution
// ---------------------------------------------------------------------------

/** True for a US ZIP (5 digits or ZIP+4). */
export function isUsPostcode(code: string): boolean {
  return /^\d{5}(?:-?\d{4})?$/.test(code.trim())
}

/**
 * Resolve a postcode to a single best {city, state, country} fill for manual
 * address entry.
 *
 * - **US**: uses the async platform reference lookup (`remote`, backed by the
 *   `USPostalCity` table). Returns `null` when the ZIP is unknown or no
 *   `remote` is available, so the caller keeps the user's typed city rather
 *   than overwriting it with a wrong guess. There is deliberately NO offline
 *   US fallback (the old ZIP-3 table was the bug).
 * - **Non-US (UK)**: the offline `lookupPostcode` area table.
 */
export async function resolvePostcodeFill(
  code: string,
  remote?: (postcode: string, country?: string) => Promise<PostcodeLookupResult[]>,
): Promise<PostcodeLookupResult | null> {
  const cleaned = code.trim()
  if (!cleaned) return null
  if (isUsPostcode(cleaned)) {
    if (!remote) return null
    try {
      const options = await remote(cleaned, 'US')
      return options[0] ?? null
    } catch {
      return null
    }
  }
  return lookupPostcode(cleaned)
}
